<template>
  <div v-if="settings">
    <div class="product-engraving__actions">
      <button
        class="product-engraving__trigger button--reset text-body-s-regular"
        type="button"
        @click="handleOpen"
      >
        <span
          class="checkbox-radio__box product-engraving__trigger-box"
          :class="{
            [$class('active')]: appliedType
          }"
        />
        <span v-text="$string('engraving.trigger_label')" />
      </button>

      <template v-if="appliedType">
        <btn
          :label="$string('engraving.edit')"
          modifiers="text"
          type="button"
          @click="handleOpen('edit')"
        />
        <span>|</span>
        <btn
          :label="$string('engraving.remove')"
          modifiers="text"
          type="button"
          @click="reset"
        />
      </template>
    </div>

    <div
      class="product-engraving__result"
      :class="{
        [$class('minimised')]: !appliedType,
      }"
    >
      <div v-if="appliedType === 'symbol'">
        <responsive-image
          :image="appliedSymbolImage?.main"
          min-max="24-48"
          sizes="24px"
        />
      </div>
      <span
        v-else-if="appliedType === 'text'"
        class="text-body-s-regular"
        v-text="appliedText"
      />
    </div>

    <teleport to="body">
      <overlay
        :key="namespace"
        type="drawer"
        :namespace="namespace"
        direction="left"
        block-class="product-engraving"
        source="engraving"
      >
        <template #title>
          <h2
            class="text-body-l-medium"
            v-text="$string('engraving.title')"
          />
        </template>

        <template #body>
          <form
            class="product-engraving__main text-body-s-regular"
            @submit.prevent="handleApply"
          >
            <!-- Engraving type -->
            <fieldset>
              <legend
                class="product-engraving__options-label text-dark-grey"
                v-text="$string('engraving.select_type')"
              />

              <div class="product-engraving__options mt-m">
                <checkbox-radio
                  :label="$string('engraving.text')"
                  type="radio"
                  name="type"
                  value="text"
                  :checked="isText"
                  @update="updateType"
                />

                <checkbox-radio
                  v-if="settings.symbols.length"
                  :label="$string('engraving.symbol')"
                  type="radio"
                  name="type"
                  value="symbol"
                  :checked="isSymbol"
                  @update="updateType"
                />
              </div>
            </fieldset>

            <hr>

            <!-- Text / symbol options -->
            <div class="text-body-m-regular">
              <transition
                name="fade"
                mode="out-in"
                @after-enter="updateOverlay"
              >
                <!-- Text -->
                <label
                  v-if="isText"
                  class="product-engraving__text"
                >
                  <span
                    class="text-dark-grey text-body-s-regular"
                    v-text="$string('engraving.add_text')"
                  />

                  <span class="product-engraving__text-input-container">
                    <input
                      v-model="text"
                      type="text"
                      name="text"
                      maxlength="3"
                    >
                    <span class="product-engraving__text-count text-body-xs-medium">
                      {{ text.length }}/3
                    </span>
                  </span>

                  <span
                    class="text-body-s-regular"
                    v-text="$string('engraving.text_hint')"
                  />
                  <span
                    v-if="textError"
                    class="text-body-s-regular product-engraving__error"
                    v-text="textError"
                  />
                </label>

                <!-- Symbols -->
                <fieldset
                  v-else
                  class="product-engraving__symbols"
                >
                  <legend
                    class="text-dark-grey text-body-s-regular"
                    v-text="getSymbolsLabel()"
                  />

                  <div class="grid mt-m">
                    <label
                      v-for="({main: image, name}) in settings.symbols"
                      :key="image.id"
                      class="col xs1 l2 product-engraving__symbol"
                      :class="{
                        [$class('active')]: symbol === name
                      }"
                    >
                      <span
                        class="visually-hidden"
                        v-text="`Select symbol ${name}`"
                      />

                      <input
                        v-model="symbol"
                        class="visually-hidden"
                        type="radio"
                        name="symbol"
                        :value="name"
                        @change="track({event: 'engraving_symbol_click', jh_value: name})"
                      >

                      <responsive-image
                        :image="image"
                        min-max="32-64"
                        sizes="32px"
                        tag="span"
                      />
                    </label>
                  </div>
                </fieldset>
              </transition>
            </div>

            <hr>

            <!-- Engraving preview image -->
            <span
              class="text-body-s-regular text-dark-grey"
              v-text="$string('engraving.preview_label')"
            />

            <div
              class="product-engraving__preview"
              :style="{
                '--preview-color': settings.previewColor
              }"
            >
              <responsive-image
                :image="settings.image"
                min-max="343-1200"
                :sizes="`(min-width: ${breakpoints.l}) 483px, 343px`"
                ratio="1:1"
              />

              <transition
                name="fade"
                mode="out-in"
              >
                <span
                  v-if="isText && text && !textError"
                  class="product-engraving__preview-text product-engraving__preview-item"
                  v-text="text"
                />
                <div
                  v-else-if="isSymbol && selectedSymbolImage"
                  class="product-engraving__preview-symbol product-engraving__preview-item"
                  :style="{
                    '--preview-size': getSymbolPreviewWidth(selectedSymbolImage)
                  }"
                >
                  <responsive-image
                    :image="selectedSymbolImage.preview"
                    min-max="32-64"
                    sizes="32px"
                  />
                </div>
              </transition>
            </div>

            <!-- Final sale agreement -->
            <checkbox-radio
              :label="$string('engraving.acknowledgement')"
              name="final-sale"
              value="final-sale"
              :checked="agree"
              @update="handleAgree"
            />

            <!-- Apply -->
            <btn
              :label="$string('engraving.apply')"
              :disabled="!isUserInputValid"
              type="submit"
              @click="track('engraving_apply_click')"
            />

            <!-- Client care footer -->
            <div class="mt-m product-engraving__footer">
              <p v-text="$string('engraving.footer')" />
              <p>
                <icon-phone />
                <span v-html="$string('engraving.phone_html')" />
              </p>
              <p>
                <icon-email />
                <span v-html="$string('engraving.email_html')" />
              </p>
            </div>
          </form>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Product engraving (product-engraving)
 * -----------------------------------------------------------------------------
 * An engraving experience on the product page where the shopper can choose
 * between text and symbols.
 *
 */

import { mapActions, mapGetters } from 'vuex'

import { track } from '~/helpers/datalayer-actions'
import { generateUUID } from '~/helpers/general'
import { getVariables, transformMetaobjectFields } from '~/helpers/graphql'

import CheckboxRadio from '~async/utils/checkbox-radio/checkbox-radio'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'

import engravingMetaobject from '~graphql/engravingMetaobject.gql'
import IconEmail from '~icons/general/email.svg'
import IconPhone from '~icons/general/phone.svg'

import breakpoints from '~/config/breakpoints'

export default {
  name: 'ProductEngraving',

  components: {
    Btn,
    CheckboxRadio,
    Overlay,
    ResponsiveImage,
    IconEmail,
    IconPhone,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      breakpoints,
      type: 'text',
      text: '',
      symbol: '',
      agree: false,
      namespace: 'engraving-' + generateUUID(),
      appliedType: false,
      settings: null,
      appliedSymbol: '',
      appliedText: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute if "apply" button should be enabled
     * @returns {boolean}
     */
    isUserInputValid() {
      if (!this.agree) {
        return false
      }

      if (this.isSymbol) {
        return Boolean(this.symbol)
      }

      return Boolean(this.text) && !this.textError
    },

    /**
     * Compute symbols image map where the key is the main image's filename (without extension)
     * and the value is the main and preview image
     * @returns {object}
     */
    symbolImages() {
      if (!this.settings) {
        return {}
      }
      return this.settings.symbols.reduce((result, current) => {
        result[current.name] = current
        return result
      }, {})
    },

    /**
     * Compute selected symbol image
     * @returns {object}
     */
    selectedSymbolImage() {
      return this.symbolImages[this.symbol]
    },

    /**
     * Compute applied symbol image
     * @returns {object}
     */
    appliedSymbolImage() {
      return this.symbolImages[this.appliedSymbol]
    },

    /**
     * Compute any text input errors
     * @returns {string}
     */
    textError() {
      const regex = /^[a-zA-Z0-9]+$/
      return !this.text || regex.test(this.text) ? '' : this.$string('engraving.text_error')
    },

    /**
     * Compute if selected type is symbol
     * @returns {boolean}
     */
    isSymbol() {
      return this.type === 'symbol'
    },

    /**
     * Compute if selected type is text
     * @returns {boolean}
     */
    isText() {
      return this.type === 'text'
    },
  },

  watch: {

    /**
     * Ensure "apply" button is tabbable
     */
    isUserInputValid(value) {
      if (value) {
        this.updateOverlay()
      }
    },

    /**
     * Get engraving settings if product changes
     */
    product() {
      this.getEngraving()
    },

    /**
     * Toggle print css when engraving is open
     */
    getActiveOverlay(overlay, oldOverlay) {
      const className = 'product-engraving-print'
      if (oldOverlay?.namespace === this.namespace) {
        document.body.classList.remove(className)
      }
      if (overlay?.namespace === this.namespace) {
        document.body.classList.add(className)
      }
    },
  },

  created() {
    this.getEngraving()
  },

  methods: {
    track,

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Open engraving drawer with agreement unchecked
     */
    async handleOpen(source) {
      this.type = this.appliedType || this.type || 'text'
      this.agree = false
      this.openOverlay({
        namespace: this.namespace,
        ignoreDismissed: true,
      })
      track(source === 'edit' ? 'engraving_edit' : 'engraving_click')
    },

    /**
     * Apply changes
     */
    handleApply() {
      if (!this.isUserInputValid) {
        return
      }
      const value = this.isSymbol ? this.symbol : this.text
      this.closeOverlay(this.namespace)
      this.$emit('apply', {
        [`_engraving_${this.type}`]: this.isSymbol ? value.replace(/ /g, '_') : value,
        [this.$string('engraving.cart.engraving_label')]: value,
        [this.$string('engraving.cart.note_label')]: this.$string('engraving.cart.note'),
      })
      this.appliedType = this.type
      this.appliedText = this.text
      this.appliedSymbol = this.symbol
    },

    /**
     * Reset engraving settings
     */
    reset() {
      this.appliedType = false
      this.$emit('apply', null)
      this.text = ''
      this.symbol = ''
      this.type = 'text'
      this.agree = false
      this.appliedText = ''
      this.appliedSymbol = ''
      track('engraving_remove_click')
    },

    /**
     * Update tabbable elements in overlay
     */
    updateOverlay() {
      cnvs.EventBus.emit('overlay:update', {
        namespace: this.namespace,
        currentElement: document.activeElement,
      })
    },

    /**
     * Get engraving with storefront api.
     * We need this for product siblings, search, and quick add
     */
    async getEngraving() {
      if (this.product.engraving?.type !== 'metaobject_reference') {
        this.settings = this.product.engraving
        return
      }

      try {
        const response = await cnvs.Query({
          query: engravingMetaobject,
          variables: getVariables({
            id: this.product.engraving.value,
          }),
        }, false)

        if (!response) {
          this.settings = null
          return
        }

        let settings = transformMetaobjectFields(response.metaobject.fields, ['internal_title'])

        // Flatten image responses
        settings.image = settings.image.image || settings.image
        settings.symbols = settings.symbols?.map((symbol) => {
          const { name, main, preview, previewWidth, shortDescription } = symbol
          return {
            main: main.image,
            preview: preview.image,
            name,
            previewWidth,
            shortDescription,
          }
        }) || []

        this.settings = settings

        if (this.isSymbol && !this.selectedSymbolImage) {
          this.appliedType = false
          this.agree = false
          this.symbol = ''
        }
      } catch (error) {
        cnvs.ReportError('[Engraving Query]', error)
      }
    },

    /**
     * Update the engraving type
     * @param {string} type - "text" or "symbol"
     */
    updateType(type) {
      this.type = type
      track({
        event: 'engraving_type_click',
        jh_value: type,
      })
    },

    /**
     * Update agree and track click
     * @param {string|boolean} value
     */
    handleAgree(value) {
      this.agree = Boolean(value)
      track({
        event: 'engraving_agree_click',
        jh_value: this.agree,
      })
    },

    /**
     * Sanitize the symbol preview size
     * @param {object} symbol
     * @returns {string}
     */
    getSymbolPreviewWidth(symbol) {
      const width = symbol.previewWidth
      if (!width) {
        return ''
      }
      return width.endsWith('%') ? width : `${width}px`
    },

    /**
     * Get the label for the symbols, which can include the selected symbol's short description.
     * @returns {string}
     */
    getSymbolsLabel() {
      let label = this.$string('engraving.select_symbol')
      const description = this.selectedSymbolImage?.shortDescription
      if (description) {
        label = `${label} ${description}`
      }
      return label
    },
  },
}

</script>

<style lang="scss">
@import './product-engraving';
</style>
